import { useState } from 'react';
import Fieldset from '~/components/commonUi/Fieldset';
import InnerLayout from '~/components/InnerLayout';
import { STAND_HEADER, S_LAG_HEADER, TOTAL_LABEL, WEB_HEADER } from '~/consts/adminReport';
import { AdminReportUserVoteCount } from '~/definitions/adminReport';
import { observer } from 'mobx-react';
import { getStore } from '~/mobx/AppStore';

interface Props {
    rows?: AdminReportUserVoteCount[];
}

const AdminReport: React.FC<Props> = ({ rows = [] }) => {
    const [hoveredRow, setHoveredRow] = useState<string>('');

    const totalWebVotes = rows.reduce((sum, row) => sum + row.user_count.web, 0);
    const totalStandVotes = rows.reduce((sum, row) => sum + row.user_count.stand, 0);

    const tableTrClass = (isHovered: boolean) => `${isHovered ? 'bg-custom-gray-04' : ''} border-b border-gray-300`;

    const tableTdClass = (isHovered: boolean, align: 'left' | 'right') =>
        `${isHovered ? 'bg-black/10' : ''} text-[82%] text-${align}`;

    const content = (
        <div className="overflow-x-auto mb-[1rem]">
            <table className="table-md w-full text-gray-900 leading-[27px]">
                {/* Table header */}
                <thead>
                    <tr className="bg-custom-gray-05 align-top">
                        <th className="text-left">{S_LAG_HEADER}</th>
                        <th className="text-right">{WEB_HEADER}</th>
                        <th className="text-right">{STAND_HEADER}</th>
                    </tr>
                </thead>

                {/* Table footer */}
                <tfoot>
                    <tr className="bg-custom-gray-05">
                        <th className="text-left">{TOTAL_LABEL}</th>
                        <th className="text-right">{totalWebVotes}</th>
                        <th className="text-right">{totalStandVotes}</th>
                    </tr>
                </tfoot>

                {/* Table body with dynamic rows */}
                <tbody>
                    {rows.map((row) => {
                        const isHovered = hoveredRow === row.id;
                        return (
                            <tr
                                key={row.id}
                                className={tableTrClass(isHovered)}
                                onMouseEnter={() => setHoveredRow(row.id)}
                                onMouseLeave={() => setHoveredRow('')}
                            >
                                <td className={tableTdClass(isHovered, 'left')}>{row.name}</td>
                                <td className={tableTdClass(isHovered, 'right')}>{row.user_count.web}</td>
                                <td className={tableTdClass(isHovered, 'right')}>{row.user_count.stand}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );

    return <InnerLayout content={<Fieldset title="Admin rapport">{content}</Fieldset>} />;
};

const AdminReportWithStore = () => {
    const { adminReportStore } = getStore();

    if (adminReportStore.userVoteCounts.length === 0) {
        return null;
    }

    return <AdminReport rows={adminReportStore.userVoteCounts} />;
};

export default observer(AdminReportWithStore);
